import * as React from "react"
import { Link } from "gatsby"
import Layout from "../components/Layout"
import Seo from "../components/SEO"
import Button from "../components/Button/Button"
import Faq from "../components/Faq/Faq"
import Reveal from "../components/Reveal/Reveal"
import ShaderBackground from "../components/ShaderBackground/ShaderBackground"
import { OfferingStyles } from "../components/Offering/OfferingStyles"
import {
  CONTACT_EMAIL,
  included,
  process,
  needs,
  baseFee,
  addOns,
  faqs,
} from "../components/Offering/offeringData"

const CustomWebsites = () => {
  return (
    <>
      <Seo title="Custom Websites" />
      <Layout>
        <OfferingStyles>
          {/* ── Hero (ambient WebGL) ─────────────────────────── */}
          <section className="o-hero">
            <ShaderBackground className="o-bg" speed={0.035} />
            <div className="o-bg__scrim" />
            <div className="o-hero__inner">
              <Reveal>
                <p className="eyebrow">Custom Websites</p>
              </Reveal>
              <Reveal delay={0.06}>
                <h1>
                  Custom websites, built from scratch
                  <span className="accent">.</span>
                </h1>
              </Reveal>
              <Reveal delay={0.14}>
                <p className="o-hero__lead">
                  For performers, artists, and creatives — designed to make a
                  strong first impression and look effortless on every screen.
                  Never a template.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="o-hero__who">
                  Built for <b>performers</b>, <b>artists</b> &amp;{" "}
                  <b>creatives</b>
                </p>
              </Reveal>
              <Reveal delay={0.26}>
                <div className="o-hero__btns">
                  <Button
                    as={Link}
                    to="/contact"
                    text="Start your project"
                    arrow
                  />
                  <Button
                    href="#pricing"
                    text="See what’s possible"
                    ghost
                    noArrow
                  />
                </div>
              </Reveal>
            </div>
          </section>

          {/* ── What's included ──────────────────────────────── */}
          <section className="band band--coal">
            <div className="band__inner">
              <Reveal className="band__head">
                <p className="eyebrow">What’s included</p>
                <h2>Everything you need, in every build.</h2>
                <p className="band__lead">
                  Every website comes with the essentials baked in — no upsells
                  for the basics.
                </p>
              </Reveal>
              <div className="included">
                {included.map((item, index) => (
                  <Reveal
                    key={index}
                    className="included__item"
                    delay={(index % 3) * 0.08}
                  >
                    <span className="included__num">{`0${index + 1}`}</span>
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* ── Process ──────────────────────────────────────── */}
          <section className="band">
            <div className="band__inner">
              <Reveal className="band__head">
                <p className="eyebrow">How it works</p>
                <h2>From first hello to launch day.</h2>
                <p className="band__lead">
                  A simple, four-step process designed to keep things clear and
                  stress-free.
                </p>
              </Reveal>
              <div className="process">
                {process.map((step, index) => (
                  <Reveal
                    key={index}
                    className="process__step"
                    delay={index * 0.08}
                  >
                    <div className="process__num">{`0${index + 1}`}</div>
                    <h3>{step.title}</h3>
                    <p>{step.body}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* ── What we need from you ────────────────────────── */}
          <section className="band band--coal">
            <div className="band__inner">
              <div className="needs">
                <Reveal className="band__head" style={{ marginBottom: 0 }}>
                  <p className="eyebrow">What we need from you</p>
                  <h2>A few things to get started.</h2>
                  <p className="band__lead">
                    The more you can share, the better — but don’t worry if
                    you’re still pulling it together.
                  </p>
                </Reveal>
                <Reveal delay={0.12}>
                  <ul className="needs__list">
                    {needs.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                  <div className="reassure" style={{ marginTop: "var(--gap)" }}>
                    <p>Don’t have it all together yet?</p>
                    <p className="reassure__sub">
                      No problem — we’ll shape it as we go. Bring what you have
                      and we’ll build around it.
                    </p>
                  </div>
                </Reveal>
              </div>
            </div>
          </section>

          {/* ── Pricing: flat fee + optional add-ons ─────────── */}
          <section className="band" id="pricing">
            <div className="band__inner">
              <Reveal className="band__head">
                <p className="eyebrow">Pricing</p>
                <h2>A flat fee, plus only what you need.</h2>
                <p className="band__lead">
                  Every project starts from one flat build fee. From there, mix
                  in the optional capabilities below — priced per project. Tell
                  us what you’re after and we’ll send a tailored quote.
                </p>
              </Reveal>

              <Reveal className="pricing__feature">
                <div className="pricing__feature-main">
                  <p className="pricing__kicker">Starting from</p>
                  <h3>{baseFee.title}</h3>
                  <p>{baseFee.body}</p>
                </div>
                <div className="pricing__feature-side">
                  <span className="pricing__amount">{baseFee.price}</span>
                  <span className="pricing__note">{baseFee.note}</span>
                  <Button
                    as={Link}
                    to="/contact"
                    text="Start your project"
                    arrow
                  />
                </div>
              </Reveal>

              <div className="addons">
                <Reveal className="addons__head">
                  <h3>Optional add-ons</h3>
                  <p>
                    Mix in only what your project needs — inquire for pricing.
                  </p>
                </Reveal>
                <div className="addons__grid">
                  {addOns.map((item, index) => (
                    <Reveal
                      key={index}
                      className="addons__item"
                      delay={(index % 2) * 0.08}
                    >
                      <span className="addons__plus" aria-hidden="true">
                        +
                      </span>
                      <div className="addons__body">
                        <h4>{item.title}</h4>
                        <p>{item.body}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ── FAQs ─────────────────────────────────────────── */}
          <section className="band band--coal">
            <div className="band__inner">
              <Reveal className="band__head">
                <p className="eyebrow">Questions</p>
                <h2>Good to know.</h2>
              </Reveal>
              <div className="o-faqs">
                {faqs.map((item, index) => (
                  <Reveal key={index} delay={index * 0.06}>
                    <Faq title={item.question} description={item.answer} />
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* ── Closing CTA (ambient WebGL) ──────────────────── */}
          <section className="band o-cta">
            <ShaderBackground className="o-bg" speed={0.045} />
            <div className="o-bg__scrim o-bg__scrim--center" />
            <div className="o-cta__inner">
              <Reveal>
                <p className="eyebrow">Let’s talk</p>
                <h2>
                  Ready to make a strong first impression
                  <span className="accent">?</span>
                </h2>
                <p className="o-cta__lead">
                  Tell us a little about you and your project — we’ll take it
                  from there.
                </p>
                <div className="o-cta__btns">
                  <Button
                    as={Link}
                    to="/contact"
                    text="Start your project"
                    arrow
                  />
                  <Button
                    href={`mailto:${CONTACT_EMAIL}`}
                    text="Email us"
                    ghost
                    noArrow
                  />
                </div>
              </Reveal>
            </div>
          </section>
        </OfferingStyles>
      </Layout>
    </>
  )
}

export default CustomWebsites
